import { useState, useEffect } from 'react';
import { accountsAPI, transactionsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Wallet, PiggyBank, BarChart3, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await accountsAPI.list();
      setAccounts(res.data.accounts);
      if (res.data.accounts.length > 0) {
        setSelectedAccount(res.data.accounts[0]);
        loadTransactions(res.data.accounts[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (accountId) => {
    try {
      const res = await transactionsAPI.list({ account_id: accountId });
      setTransactions(res.data.transactions);
    } catch (err) {
      console.error('Failed to load transactions');
    }
  };

  const selectAccount = (acc) => {
    setSelectedAccount(acc);
    loadTransactions(acc._id);
  };

  const getIcon = (type) => {
    if (type === 'checking') return <Wallet className="h-5 w-5 text-[#0B5CAB]" />;
    if (type === 'savings') return <PiggyBank className="h-5 w-5 text-green-600" />;
    return <BarChart3 className="h-5 w-5 text-purple-600" />;
  };

  if (loading) {
    return <div className="page-container"><Skeleton className="h-96 rounded-xl" /></div>;
  }

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Accounts</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {accounts.map(acc => (
          <div
            key={acc._id}
            className={`account-card cursor-pointer ${selectedAccount?._id === acc._id ? 'ring-2 ring-[#0B5CAB]' : ''}`}
            onClick={() => selectAccount(acc)}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${acc.type === 'checking' ? 'bg-blue-50' : acc.type === 'savings' ? 'bg-green-50' : 'bg-purple-50'}`}>
                {getIcon(acc.type)}
              </div>
              <div>
                <p className="text-sm font-medium">{acc.name}</p>
                <p className="text-xs text-muted-foreground">{acc.account_number}</p>
              </div>
            </div>
            <p className="text-2xl font-semibold tabular-nums">{formatCurrency(acc.available_balance)}</p>
            {acc.type === 'investment' && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-green-600 font-medium"><ArrowUpRight className="h-3 w-3 inline" /> +{acc.ytd_return}% YTD</span>
                <span className="text-xs text-green-600">+{formatCurrency(acc.todays_deposit)} today</span>
              </div>
            )}
            {acc.type === 'savings' && acc.interest_rate && (
              <Badge variant="secondary" className="mt-2 text-xs">{acc.interest_rate}% APY</Badge>
            )}
          </div>
        ))}
      </div>

      {/* Account Transactions */}
      {selectedAccount && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>
              {selectedAccount.name} - Transaction History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">No transactions found for this account</p>
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map(tx => (
                  <div key={tx._id} className="flex items-center justify-between py-3 px-3 rounded-lg transaction-row">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{tx.description}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(tx.date)} &middot; {tx.category}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold tabular-nums ${tx.amount >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                        {tx.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                      </p>
                      <p className="text-xs text-muted-foreground">Bal: {formatCurrency(tx.balance_after)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
